/* =========================================================
   SUNDA TRAVEL
   SGJ PRODUCT PAGE ENGINE

   Version:
   3.0 FINAL

   Features:
   ✓ Radiant Theme compatible
   ✓ .page-wrapper scroll support
   ✓ Fixed Product Navigation
   ✓ Header-aware offset
   ✓ Placeholder height preservation
   ✓ Smooth anchor navigation
   ✓ Active navigation tracking
   ✓ Mobile horizontal navigation
   ✓ Scroll reveal
   ✓ Shopify Section Reload compatible
   ✓ Duplicate initialization protection
========================================================= */

(function () {

  'use strict';


  /* =======================================================
     GLOBAL CONFIG
  ======================================================= */

  const CONFIG = {

    navSelector:
      '#sgj-product-nav',

    pageSelector:
      '.sgj-page',

    scrollContainerSelector:
      '.page-wrapper',

    headerSelectors: [
      '.sunda-luxury-header',
      '.sunda-header',
      '#header-group',
      '.shopify-section-header',
      '.header-group'
    ],

    sections: [
      'sgj-overview',
      'sgj-map',
      'sgj-experiences',
      'sgj-itinerary',
      'sgj-included',
      'sgj-why',
      'sgj-faq'
    ],

    desktopMinTop:
      100,

    tabletMinTop:
      90,

    mobileMinTop:
      77,

    scrollOffset:
      16,

    activeThreshold:
      120,

    revealThreshold:
      0.12

  };


  /* =======================================================
     GLOBAL STATE
  ======================================================= */

  const STATE = {

    initialized:
      false,

    nav:
      null,

    navSection:
      null,

    placeholder:
      null,

    scrollContainer:
      null,

    fixed:
      false,

    originalTop:
      0,

    originalLeft:
      0,

    originalWidth:
      0,

    navHeight:
      0,

    ticking:
      false,

    resizeTimer:
      null,

    activeId:
      null

  };


  /* =======================================================
     GET SCROLL CONTAINER
  ======================================================= */

  function getScrollContainer() {

    const wrapper =
      document.querySelector(
        CONFIG.scrollContainerSelector
      );


    /*
      Radiant Theme desktop:
      .page-wrapper is the real
      scrolling container.
    */

    if (
      wrapper &&
      wrapper.scrollHeight >
      wrapper.clientHeight + 5
    ) {

      return wrapper;

    }


    return window;

  }


  /* =======================================================
     GET CURRENT SCROLL TOP
  ======================================================= */

  function getScrollTop() {

    const container =
      STATE.scrollContainer;


    if (
      container &&
      container !== window
    ) {

      return container.scrollTop;

    }


    return (
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0
    );

  }


  /* =======================================================
     GET HEADER
  ======================================================= */

  function getHeader() {

    for (
      let i = 0;
      i < CONFIG.headerSelectors.length;
      i++
    ) {

      const header =
        document.querySelector(
          CONFIG.headerSelectors[i]
        );


      if (
        header &&
        header.getBoundingClientRect().height > 0
      ) {

        return header;

      }

    }


    return null;

  }


  /* =======================================================
     GET HEADER HEIGHT
  ======================================================= */

  function getHeaderHeight() {

    const header =
      getHeader();


    if (!header) {

      return 0;

    }


    return Math.ceil(
      header.getBoundingClientRect().height
    );

  }


  /* =======================================================
     GET NAV TOP OFFSET
  ======================================================= */

  function getNavTop() {

    const width =
      window.innerWidth;


    const headerHeight =
      getHeaderHeight();


    let minimum;


    if (
      width <= 768
    ) {

      minimum =
        CONFIG.mobileMinTop;

    } else if (
      width <= 1200
    ) {

      minimum =
        CONFIG.tabletMinTop;

    } else {

      minimum =
        CONFIG.desktopMinTop;

    }


    /*
      Header-aware.
      Never allow nav to overlap header.
    */

    const calculated =
      headerHeight > 0
        ? headerHeight + 10
        : minimum;


    return Math.max(
      calculated,
      minimum
    );

  }


  /* =======================================================
     UPDATE CSS VARIABLE
  ======================================================= */

  function updateNavTop() {

    const top =
      getNavTop();


    document.documentElement.style
      .setProperty(
        '--sgj-nav-top',
        `${top}px`
      );


    return top;

  }


  /* =======================================================
     FIND NAV SECTION
  ======================================================= */

  function getNavSection() {

    if (!STATE.nav) {

      return null;

    }


    /*
      The nav itself lives inside
      a Shopify Section.

      Find the closest section wrapper.
    */

    const section =
      STATE.nav.closest(
        '.shopify-section'
      );


    return section || null;

  }


  /* =======================================================
     CREATE PLACEHOLDER
  ======================================================= */

  function createPlaceholder() {

    if (
      !STATE.nav ||
      STATE.placeholder
    ) {

      return;

    }


    const placeholder =
      document.createElement(
        'div'
      );


    placeholder.className =
      'sgj-product-nav-placeholder';


    placeholder.setAttribute(
      'aria-hidden',
      'true'
    );


    STATE.nav.parentNode.insertBefore(
      placeholder,
      STATE.nav.nextSibling
    );


    STATE.placeholder =
      placeholder;

  }


  /* =======================================================
     UPDATE PLACEHOLDER
  ======================================================= */

  function updatePlaceholder() {

    if (
      !STATE.placeholder ||
      !STATE.nav
    ) {

      return;

    }


    STATE.navHeight =
      STATE.nav.offsetHeight;


    STATE.placeholder.style.height =
      `${STATE.navHeight}px`;

  }


  /* =======================================================
     GET NAV DOCUMENT POSITION
  ======================================================= */

  function calculateOriginalPosition() {

    if (
      !STATE.nav ||
      STATE.fixed
    ) {

      return;

    }


    const navRect =
      STATE.nav.getBoundingClientRect();


    const container =
      STATE.scrollContainer;


    if (
      container &&
      container !== window
    ) {

      const containerRect =
        container.getBoundingClientRect();


      STATE.originalTop =
        navRect.top -
        containerRect.top +
        container.scrollTop;


      STATE.originalLeft =
        navRect.left;


      STATE.originalWidth =
        navRect.width;

    } else {

      STATE.originalTop =
        navRect.top +
        window.pageYOffset;


      STATE.originalLeft =
        navRect.left;


      STATE.originalWidth =
        navRect.width;

    }

  }


  /* =======================================================
     FIX NAV
  ======================================================= */

  function setFixed() {

    if (
      STATE.fixed ||
      !STATE.nav
    ) {

      return;

    }


    updatePlaceholder();


    /*
      Preserve page height.
    */

    if (
      STATE.placeholder
    ) {

      STATE.placeholder.style.height =
        `${STATE.navHeight}px`;

      STATE.placeholder.classList.add(
        'is-active'
      );

    }


    const navRect =
      STATE.nav.getBoundingClientRect();


    /*
      Fixed width must match the
      original Shopify Section width.
    */

    STATE.nav.style.width =
      `${navRect.width}px`;


    STATE.nav.style.left =
      `${navRect.left}px`;


    STATE.nav.classList.add(
      'is-fixed'
    );


    STATE.nav.classList.add(
      'is-scrolled'
    );


    STATE.fixed =
      true;

  }


  /* =======================================================
     UNFIX NAV
  ======================================================= */

  function removeFixed() {

    if (
      !STATE.fixed ||
      !STATE.nav
    ) {

      return;

    }


    STATE.nav.classList.remove(
      'is-fixed'
    );


    STATE.nav.classList.remove(
      'is-scrolled'
    );


    STATE.nav.style.width =
      '';

    STATE.nav.style.left =
      '';


    if (
      STATE.placeholder
    ) {

      STATE.placeholder.classList.remove(
        'is-active'
      );

    }


    STATE.fixed =
      false;


    /*
      Recalculate natural position.
    */

    requestAnimationFrame(
      function () {

        calculateOriginalPosition();

      }
    );

  }


  /* =======================================================
     UPDATE FIXED STATE
  ======================================================= */

  function updateFixedState() {

    if (
      !STATE.nav
    ) {

      return;

    }


    const scrollTop =
      getScrollTop();


    const navTop =
      getNavTop();


    /*
      Natural document position
      of Product Nav.
    */

    const distanceFromViewportTop =
      STATE.originalTop -
      scrollTop;


    if (
      distanceFromViewportTop <=
      navTop
    ) {

      setFixed();

    } else {

      removeFixed();

    }

  }


  /* =======================================================
     SCHEDULE SCROLL UPDATE
  ======================================================= */

  function requestScrollUpdate() {

    if (
      STATE.ticking
    ) {

      return;

    }


    STATE.ticking =
      true;


    requestAnimationFrame(
      function () {

        updateFixedState();

        updateActiveNavigation();

        STATE.ticking =
          false;

      }
    );

  }


  /* =======================================================
     GET TARGET ELEMENT
  ======================================================= */

  function getTarget(
    id
  ) {

    if (!id) {

      return null;

    }


    return document.getElementById(
      id
    );

  }


  /* =======================================================
     GET TARGET TOP INSIDE SCROLL CONTAINER
  ======================================================= */

  function getTargetScrollTop(
    target
  ) {

    if (!target) {

      return 0;

    }


    const container =
      STATE.scrollContainer;


    const targetRect =
      target.getBoundingClientRect();


    const navTop =
      getNavTop();


    if (
      container &&
      container !== window
    ) {

      const containerRect =
        container.getBoundingClientRect();


      return (
        targetRect.top -
        containerRect.top +
        container.scrollTop -
        navTop -
        CONFIG.scrollOffset
      );

    }


    return (
      targetRect.top +
      window.pageYOffset -
      navTop -
      CONFIG.scrollOffset
    );

  }


  /* =======================================================
     SCROLL TO TARGET
  ======================================================= */

  function scrollToTarget(
    id
  ) {

    const target =
      getTarget(id);


    if (!target) {

      return;

    }


    const destination =
      Math.max(
        0,
        getTargetScrollTop(
          target
        )
      );


    const container =
      STATE.scrollContainer;


    if (
      container &&
      container !== window
    ) {

      container.scrollTo({

        top:
          destination,

        behavior:
          'smooth'

      });

    } else {

      window.scrollTo({

        top:
          destination,

        behavior:
          'smooth'

      });

    }


    /*
      Update URL without
      triggering browser jump.
    */

    if (
      window.history &&
      window.history.replaceState
    ) {

      window.history.replaceState(
        null,
        '',
        `#${id}`
      );

    }

  }


  /* =======================================================
     NAV CLICK
  ======================================================= */

  function handleNavClick(
    event
  ) {

    const link =
      event.currentTarget;


    if (!link) {

      return;

    }


    const targetId =
      link.dataset.target;


    if (!targetId) {

      return;

    }


    const target =
      getTarget(
        targetId
      );


    if (!target) {

      return;

    }


    event.preventDefault();


    setActiveNavigation(
      targetId
    );


    scrollToTarget(
      targetId
    );

  }


  /* =======================================================
     SET ACTIVE NAV
  ======================================================= */

  function setActiveNavigation(
    id
  ) {

    if (
      !STATE.nav
    ) {

      return;

    }


    const links =
      STATE.nav.querySelectorAll(
        '.sgj-product-nav__link'
      );


    links.forEach(
      function (link) {

        const active =
          link.dataset.target === id;


        link.classList.toggle(
          'is-active',
          active
        );


        if (active) {

          link.setAttribute(
            'aria-current',
            'page'
          );

        } else {

          link.removeAttribute(
            'aria-current'
          );

        }

      }
    );


    STATE.activeId =
      id;


    /*
      Mobile:
      automatically bring active tab
      into view.
    */

    const activeLink =
      STATE.nav.querySelector(
        '.sgj-product-nav__link.is-active'
      );


    if (
      activeLink &&
      window.innerWidth <= 768
    ) {

      activeLink.scrollIntoView({

        behavior:
          'smooth',

        block:
          'nearest',

        inline:
          'center'

      });

    }

  }


  /* =======================================================
     DETERMINE ACTIVE SECTION
  ======================================================= */

  function updateActiveNavigation() {

    if (
      !STATE.nav
    ) {

      return;

    }


    const container =
      STATE.scrollContainer;


    const navTop =
      getNavTop();


    const activeLine =
      navTop +
      CONFIG.activeThreshold;


    let currentId =
      CONFIG.sections[0];


    let bestDistance =
      Infinity;


    CONFIG.sections.forEach(
      function (id) {

        const section =
          document.getElementById(
            id
          );


        if (!section) {

          return;

        }


        const rect =
          section.getBoundingClientRect();


        const top =
          rect.top;


        /*
          Prefer sections that have
          already reached the active line.
        */

        if (
          top <= activeLine
        ) {

          const distance =
            Math.abs(
              activeLine - top
            );


          if (
            distance < bestDistance
          ) {

            bestDistance =
              distance;

            currentId =
              id;

          }

        }

      }
    );


    /*
      At the very bottom,
      keep FAQ active until CTA.
    */

    const lastSection =
      document.getElementById(
        CONFIG.sections[
          CONFIG.sections.length - 1
        ]
      );


    if (
      lastSection
    ) {

      const lastRect =
        lastSection.getBoundingClientRect();


      const viewportHeight =
        container &&
        container !== window
          ? container.clientHeight
          : window.innerHeight;


      if (
        lastRect.top <
        viewportHeight * .75
      ) {

        currentId =
          CONFIG.sections[
            CONFIG.sections.length - 1
          ];

      }

    }


    if (
      currentId !==
      STATE.activeId
    ) {

      setActiveNavigation(
        currentId
      );

    }

  }


  /* =======================================================
     SCROLL REVEAL
  ======================================================= */

  function initReveal() {

    const elements =
      document.querySelectorAll(
        '.sgj-reveal'
      );


    if (
      !elements.length
    ) {

      return;

    }


    /*
      IntersectionObserver works
      with the Radiant .page-wrapper.
    */

    const root =
      STATE.scrollContainer !== window
        ? STATE.scrollContainer
        : null;


    if (
      'IntersectionObserver'
      in window
    ) {

      const observer =
        new IntersectionObserver(
          function (
            entries
          ) {

            entries.forEach(
              function (
                entry
              ) {

                if (
                  entry.isIntersecting
                ) {

                  entry.target.classList.add(
                    'sgj-reveal-visible'
                  );


                  observer.unobserve(
                    entry.target
                  );

                }

              }
            );

          },
          {

            root:
              root,

            threshold:
              CONFIG.revealThreshold

          }
        );


      elements.forEach(
        function (
          element
        ) {

          observer.observe(
            element
          );

        }
      );

    } else {

      elements.forEach(
        function (
          element
        ) {

          element.classList.add(
            'sgj-reveal-visible'
          );

        }
      );

    }

  }


  /* =======================================================
     INIT NAV LINKS
  ======================================================= */

  function initNavLinks() {

    if (
      !STATE.nav
    ) {

      return;

    }


    const links =
      STATE.nav.querySelectorAll(
        '.sgj-product-nav__link[data-target]'
      );


    links.forEach(
      function (link) {

        /*
          Prevent duplicate listeners.
        */

        if (
          link.dataset.sgjBound === 'true'
        ) {

          return;

        }


        link.dataset.sgjBound =
          'true';


        link.addEventListener(
          'click',
          handleNavClick
        );

      }
    );

  }


  /* =======================================================
     INITIAL HASH
  ======================================================= */

  function handleInitialHash() {

    const hash =
      window.location.hash;


    if (!hash) {

      return;

    }


    const id =
      hash.substring(1);


    if (
      CONFIG.sections.includes(
        id
      )
    ) {

      setTimeout(
        function () {

          scrollToTarget(
            id
          );

        },
        300
      );

    }

  }


  /* =======================================================
     RESIZE
  ======================================================= */

  function handleResize() {

    clearTimeout(
      STATE.resizeTimer
    );


    STATE.resizeTimer =
      setTimeout(
        function () {

          updateNavTop();


          /*
            Temporarily release fixed state
            so dimensions can be recalculated.
          */

          if (
            STATE.fixed
          ) {

            STATE.nav.classList.remove(
              'is-fixed'
            );

            STATE.nav.classList.remove(
              'is-scrolled'
            );

            STATE.nav.style.width =
              '';

            STATE.nav.style.left =
              '';

            STATE.fixed =
              false;

          }


          if (
            STATE.placeholder
          ) {

            STATE.placeholder.classList.remove(
              'is-active'
            );

          }


          updatePlaceholder();


          requestAnimationFrame(
            function () {

              calculateOriginalPosition();

              updateFixedState();

              updateActiveNavigation();

            }
          );

        },
        150
      );

  }


  /* =======================================================
     BIND SCROLL EVENTS
  ======================================================= */

  function bindScrollEvents() {

    const container =
      STATE.scrollContainer;


    if (
      container &&
      container !== window
    ) {

      if (
        container.dataset.sgjScrollBound !==
        'true'
      ) {

        container.dataset.sgjScrollBound =
          'true';


        container.addEventListener(
          'scroll',
          requestScrollUpdate,
          {
            passive: true
          }
        );

      }

    }


    /*
      Keep window listener too.
      Useful for mobile / fallback.
    */

    if (
      !window.__sgjWindowScrollBound
    ) {

      window.__sgjWindowScrollBound =
        true;


      window.addEventListener(
        'scroll',
        requestScrollUpdate,
        {
          passive: true
        }
      );

    }

  }


  /* =======================================================
     MAIN INITIALIZATION
  ======================================================= */

  function initSgjProductPage() {

    const nav =
      document.querySelector(
        CONFIG.navSelector
      );


    if (!nav) {

      return;

    }


    /*
      If already initialized,
      refresh dimensions instead.
    */

    if (
      STATE.initialized &&
      STATE.nav === nav
    ) {

      updateNavTop();

      updatePlaceholder();

      requestAnimationFrame(
        function () {

          calculateOriginalPosition();

          updateFixedState();

          updateActiveNavigation();

        }
      );

      return;

    }


    /*
      Reset state.
    */

    STATE.initialized =
      false;

    STATE.nav =
      nav;

    STATE.fixed =
      false;

    STATE.activeId =
      null;


    /*
      Detect real scroll container.
    */

    STATE.scrollContainer =
      getScrollContainer();


    /*
      Find Shopify Section.
    */

    STATE.navSection =
      getNavSection();


    /*
      Create placeholder.
    */

    STATE.placeholder =
      null;

    createPlaceholder();


    /*
      Update offset.
    */

    updateNavTop();


    /*
      Wait for layout.
    */

    requestAnimationFrame(
      function () {

        updatePlaceholder();

        calculateOriginalPosition();

        initNavLinks();

        bindScrollEvents();

        initReveal();

        setActiveNavigation(
          'sgj-overview'
        );

        updateFixedState();

        updateActiveNavigation();

        STATE.initialized =
          true;

        handleInitialHash();

      }
    );

  }


  /* =======================================================
     PUBLIC API
  ======================================================= */

  window.SundaSGJ = {

    init:
      initSgjProductPage,

    refresh:
      function () {

        if (
          STATE.nav
        ) {

          updateNavTop();

          updatePlaceholder();

          calculateOriginalPosition();

          updateFixedState();

          updateActiveNavigation();

        }

      },

    scrollTo:
      scrollToTarget

  };


  /* =======================================================
     DOM READY
  ======================================================= */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initSgjProductPage
    );

  } else {

    initSgjProductPage();

  }


  /* =======================================================
     RESIZE
  ======================================================= */

  window.addEventListener(
    'resize',
    handleResize,
    {
      passive: true
    }
  );


  /* =======================================================
     SHOPIFY SECTION LOAD
  ======================================================= */

  document.addEventListener(
    'shopify:section:load',
    function (event) {

      if (
        event.target &&
        event.target.querySelector(
          CONFIG.navSelector
        )
      ) {

        setTimeout(
          initSgjProductPage,
          50
        );

      }

    }
  );


  /* =======================================================
     SHOPIFY SECTION SELECT
  ======================================================= */

  document.addEventListener(
    'shopify:section:select',
    function () {

      setTimeout(
        function () {

          if (
            window.SundaSGJ
          ) {

            window.SundaSGJ.refresh();

          }

        },
        50
      );

    }
  );


})();